import { useEffect, useState } from "react";
import ErrorUtils from "src/utils/ErrorUtils";
import StringUtils from "src/utils/stringUtils";
import apiService from "../ApiCallService";
import {
    DAS_DRAFT_VERSION,
    DAS_LIVE_VERSION,
    DerivedAllocationSheetData,
    ListDerivedAllocationSheetsResponse
} from "src/models/derived-allocation-sheet/DerivedAllocationSheet";
import { CreateDerivedAllocationSheetRequest } from "src/models/derived-allocation-sheet/CreateDerivedAllocationSheetRequest";
import { CreateDerivedAllocationSheetResponse } from "src/models/derived-allocation-sheet/CreateDerivedAllocationSheetResponse";
import { UpdateDerivedAllocationSheetRequest } from "src/models/derived-allocation-sheet/UpdateDerivedAllocationSheetRequest";
import { UpdateDerivedAllocationSheetResponse } from "src/models/derived-allocation-sheet/UpdateDerivedAllocationSheetResponse";
import { GetDerivedAllocationSheetResponse } from "src/models/derived-allocation-sheet/GetDerivedAllocationSheetResponse";
import { GetDerivedAllocationSheetRequest } from "src/models/derived-allocation-sheet/GetDerivedAllocationSheetRequest";
import { StatusError } from "src/models/common/StatusError";
import {
    CreateDASDataSourceRequest, CreateDASDataSourceResponse,
    UpdateDASDataSourceRequest, UpdateDASDataSourceResponse,
    DeleteDASDataSourceRequest, DeleteDASDataSourceResponse, GetDASDataSourcesResponse
} from "src/models/derived-allocation-sheet/DASDataSource";
import {
    CreateDASTotalRequest, CreateDASTotalResponse,
    UpdateDASTotalRequest, UpdateDASTotalResponse,
    DeleteDASTotalResponse, GetDASTotalsResponse
} from "src/models/derived-allocation-sheet/DASTotal";
import {
    CreateDASAllocationGroupRequest, CreateDASAllocationGroupResponse,
    UpdateDASAllocationGroupRequest, UpdateDASAllocationGroupResponse,
    DeleteDASAllocationGroupResponse, GetDASAllocationGroupsResponse
} from "src/models/derived-allocation-sheet/DASAllocationGroup";
import {
    CreateDASAllocationFormulaRequest, CreateDASAllocationFormulaResponse,
    UpdateDASAllocationFormulaRequest, UpdateDASAllocationFormulaResponse,
    DeleteDASAllocationFormulaResponse, GetDASAllocationFormulasResponse
} from "src/models/derived-allocation-sheet/DASAllocationFormula";
import {
    ExecuteDerivedAllocationSheetRequest, ExecuteDerivedAllocationSheetResponse,
    GetDerivedAllocationSheetExecutionStatusRequest, GetDerivedAllocationSheetExecutionStatusResponse,
    DownloadDerivedAllocationSheetRequest, DownloadDerivedAllocationSheetResponse
} from "src/models/derived-allocation-sheet/DASLifecycle";
import {
    SetDASMappingRequest, SetDASMappingResponse
} from "src/models/derived-allocation-sheet/SetDASMapping";

/** True for a missing version: a 404, or a user-facing error saying the version does not exist. */
const isNotFoundError = (ex: unknown): boolean => ex instanceof StatusError
    && (ex.status === 404 || (ex.status === 417 && /not (be )?found|does not exist/i.test(ex.message)));

/**
 * Service for the Derived Allocation Sheet (DAS) module. Mirrors the shape of
 * {@link MultidimensionalAllocationService} so callers get the same hook-based tuple return
 * pattern across both sheet types.
 */
export default class DerivedAllocationSheetService {

    /**
     * Lists DAS sheets, optionally filtered by status.
     */
    listDerivedAllocationSheets(query: string, status?: string): [ListDerivedAllocationSheetsResponse | null, boolean, string] {
        const [result, setResult] = useState(null as ListDerivedAllocationSheetsResponse | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.getDerivedAllocationSheets(status);
                    const json = await response.json() as any;
                    const transformed: ListDerivedAllocationSheetsResponse = {
                        ...json, sheets: json.sheets ? this.transformSheetList(json.sheets) : []
                    };
                    if (active) setResult(transformed);
                } catch (ex) {
                    if (active) setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (active) setLoading(false);
                }
            };
            if (!StringUtils.isNullOrEmpty(query)) {
                fetchData();
            }
            return () => { active = false; };
        }, [query, status]);

        return [result, loading, error];
    }

    /**
     * Gets a single DAS at a specific version. `availableVersions` is returned alongside so the
     * detail page can render a version picker.
     */
    getDerivedAllocationSheet(query: string, request: GetDerivedAllocationSheetRequest):
        [DerivedAllocationSheetData | null, boolean, string, number[]] {
        const { sheetId, version } = request;
        const [result, setResult] = useState(null as DerivedAllocationSheetData | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [availableVersions, setAvailableVersions] = useState<number[]>([]);

        useEffect(() => {
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.getDerivedAllocationSheet({ sheetId, version });
                    const json = await response.json() as GetDerivedAllocationSheetResponse;
                    if (active) {
                        setResult(json.sheetData ? this.transformSingleSheet(json.sheetData) : null);
                        setAvailableVersions(json.availableVersions ?? []);
                    }
                } catch (ex) {
                    if (active) {
                        setError(ErrorUtils.getMessage(ex));
                        setResult(null);
                        setAvailableVersions([]);
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };
            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(sheetId)) {
                fetchData();
            }
            return () => { active = false; };
        }, [query, sheetId, version]);

        return [result, loading, error, availableVersions];
    }

    /**
     * Reads the version a user works on when a sheet is first opened: the draft when there is one, else
     * the live version. A sheet that was never activated has no live version, so the draft is read
     * first. Only an empty or not-found draft falls back to the live version; any other draft error is
     * returned as is.
     */
    async getWorkingDerivedAllocationSheet(request: Pick<GetDerivedAllocationSheetRequest, 'sheetId'>): Promise<GetDerivedAllocationSheetResponse> {
        const { sheetId } = request;
        try {
            const draft = await this.getDerivedAllocationSheetAt({ sheetId, version: DAS_DRAFT_VERSION });
            if (draft.sheetData) {
                return draft;
            }
        } catch (ex) {
            if (!isNotFoundError(ex)) {
                throw ex;
            }
        }
        return this.getDerivedAllocationSheetAt({ sheetId, version: DAS_LIVE_VERSION });
    }

    /**
     * Reads one version of a sheet outside a render, for example to refresh the header after a write.
     */
    async getDerivedAllocationSheetAt(request: GetDerivedAllocationSheetRequest): Promise<GetDerivedAllocationSheetResponse> {
        const response = await apiService.getDerivedAllocationSheet(request);
        const json = await response.json() as GetDerivedAllocationSheetResponse;
        return {
            sheetData: json?.sheetData ? this.transformSingleSheet(json.sheetData) : null,
            availableVersions: json?.availableVersions ?? [],
        };
    }

    /**
     * Creates a new DAS. When `sourceSheetId` is set on the request, the backend clones from
     * that source (all Step-1/2/3/4 rows + mapping pointer deep-copied).
     */
    async createDerivedAllocationSheet(request: CreateDerivedAllocationSheetRequest): Promise<CreateDerivedAllocationSheetResponse> {
        const response = await apiService.createDerivedAllocationSheet(request);
        const json = await response.json() as CreateDerivedAllocationSheetResponse;
        return { sheetData: json.sheetData ? this.transformSingleSheet(json.sheetData) : null };
    }

    /**
     * Updates DAS metadata (name, description). Row edits go through dedicated row ops.
     */
    async updateDerivedAllocationSheet(request: UpdateDerivedAllocationSheetRequest): Promise<UpdateDerivedAllocationSheetResponse> {
        const response = await apiService.updateDerivedAllocationSheet(request);
        const json = await response.json() as UpdateDerivedAllocationSheetResponse;
        return { sheetData: json.sheetData ? this.transformSingleSheet(json.sheetData) : null };
    }

getDASDataSources(query: string, sheetId: string, version: number): [GetDASDataSourcesResponse | null, boolean, string] {
        const [result, setResult] = useState<GetDASDataSourcesResponse | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.getDASDataSources(sheetId, version);
                    const json = await response.json() as GetDASDataSourcesResponse;
                    if (active) setResult({ dataSources: json?.dataSources ?? [] });
                } catch (ex) {
                    if (active) {
                        setError(ErrorUtils.getMessage(ex));
                        setResult(null);
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };
            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(sheetId)) {
                fetchData();
            }
            return () => { active = false; };
        }, [query, sheetId, version]);

        return [result, loading, error];
    }

    async createDASDataSource(request: CreateDASDataSourceRequest): Promise<CreateDASDataSourceResponse> {
        const response = await apiService.createDASDataSource(request);
        const json = await response.json() as CreateDASDataSourceResponse;
        return { dataSource: json?.dataSource ?? null };
    }

    async updateDASDataSource(request: UpdateDASDataSourceRequest): Promise<UpdateDASDataSourceResponse> {
        const response = await apiService.updateDASDataSource(request);
        const json = await response.json() as UpdateDASDataSourceResponse;
        return { dataSource: json?.dataSource ?? null };
    }

    async deleteDASDataSource(request: DeleteDASDataSourceRequest): Promise<DeleteDASDataSourceResponse> {
        const response = await apiService.deleteDASDataSource(request);
        const json = await response.json() as DeleteDASDataSourceResponse;
        return { dataSource: json?.dataSource ?? null };
    }

getDASTotals(query: string, sheetId: string, version: number): [GetDASTotalsResponse | null, boolean, string] {
        const [result, setResult] = useState<GetDASTotalsResponse | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.getDASTotals(sheetId, version);
                    const json = await response.json() as GetDASTotalsResponse;
                    if (active) setResult({ totals: json?.totals ?? [] });
                } catch (ex) {
                    if (active) {
                        setError(ErrorUtils.getMessage(ex));
                        setResult(null);
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };
            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(sheetId)) {
                fetchData();
            }
            return () => { active = false; };
        }, [query, sheetId, version]);

        return [result, loading, error];
    }

    async createDASTotal(request: CreateDASTotalRequest): Promise<CreateDASTotalResponse> {
        const response = await apiService.createDASTotal(request);
        const json = await response.json() as CreateDASTotalResponse;
        return { total: json?.total ?? null };
    }

    async updateDASTotal(request: UpdateDASTotalRequest): Promise<UpdateDASTotalResponse> {
        const response = await apiService.updateDASTotal(request);
        const json = await response.json() as UpdateDASTotalResponse;
        return { total: json?.total ?? null };
    }

    async deleteDASTotal(sheetId: string, totalId: string): Promise<DeleteDASTotalResponse> {
        const response = await apiService.deleteDASTotal(sheetId, totalId);
        const json = await response.json() as DeleteDASTotalResponse;
        return { total: json?.total ?? null };
    }

getDASAllocationGroups(query: string, sheetId: string, version: number): [GetDASAllocationGroupsResponse | null, boolean, string] {
        const [result, setResult] = useState<GetDASAllocationGroupsResponse | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.getDASAllocationGroups(sheetId, version);
                    const json = await response.json() as GetDASAllocationGroupsResponse;
                    if (active) setResult({ allocationGroups: json?.allocationGroups ?? [] });
                } catch (ex) {
                    if (active) {
                        setError(ErrorUtils.getMessage(ex));
                        setResult(null);
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };
            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(sheetId)) {
                fetchData();
            }
            return () => { active = false; };
        }, [query, sheetId, version]);

        return [result, loading, error];
    }

    async createDASAllocationGroup(request: CreateDASAllocationGroupRequest): Promise<CreateDASAllocationGroupResponse> {
        const response = await apiService.createDASAllocationGroup(request);
        const json = await response.json() as CreateDASAllocationGroupResponse;
        return { allocationGroup: json?.allocationGroup ?? null };
    }

    async updateDASAllocationGroup(request: UpdateDASAllocationGroupRequest): Promise<UpdateDASAllocationGroupResponse> {
        const response = await apiService.updateDASAllocationGroup(request);
        const json = await response.json() as UpdateDASAllocationGroupResponse;
        return { allocationGroup: json?.allocationGroup ?? null };
    }

    async deleteDASAllocationGroup(sheetId: string, allocationGroupId: string): Promise<DeleteDASAllocationGroupResponse> {
        const response = await apiService.deleteDASAllocationGroup(sheetId, allocationGroupId);
        const json = await response.json() as DeleteDASAllocationGroupResponse;
        return { allocationGroup: json?.allocationGroup ?? null };
    }

getDASAllocationFormulas(query: string, sheetId: string, version: number, allocationGroupId: string): [GetDASAllocationFormulasResponse | null, boolean, string] {
        const [result, setResult] = useState<GetDASAllocationFormulasResponse | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            let active = true;
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.getDASAllocationFormulas(sheetId, version, allocationGroupId);
                    const json = await response.json() as GetDASAllocationFormulasResponse;
                    if (active) setResult({ allocationFormulas: json?.allocationFormulas ?? [] });
                } catch (ex) {
                    if (active) {
                        setError(ErrorUtils.getMessage(ex));
                        setResult(null);
                    }
                } finally {
                    if (active) setLoading(false);
                }
            };
            if (!StringUtils.isNullOrEmpty(query) && !StringUtils.isNullOrEmpty(sheetId) && !StringUtils.isNullOrEmpty(allocationGroupId)) {
                fetchData();
            }
            return () => { active = false; };
        }, [query, sheetId, version, allocationGroupId]);

        return [result, loading, error];
    }

    async createDASAllocationFormula(request: CreateDASAllocationFormulaRequest): Promise<CreateDASAllocationFormulaResponse> {
        const response = await apiService.createDASAllocationFormula(request);
        const json = await response.json() as CreateDASAllocationFormulaResponse;
        return { allocationFormula: json?.allocationFormula ?? null };
    }

    async updateDASAllocationFormula(request: UpdateDASAllocationFormulaRequest): Promise<UpdateDASAllocationFormulaResponse> {
        const response = await apiService.updateDASAllocationFormula(request);
        const json = await response.json() as UpdateDASAllocationFormulaResponse;
        return { allocationFormula: json?.allocationFormula ?? null };
    }

    async deleteDASAllocationFormula(sheetId: string, allocationGroupId: string, allocationFormulaId: string): Promise<DeleteDASAllocationFormulaResponse> {
        const response = await apiService.deleteDASAllocationFormula(sheetId, allocationGroupId, allocationFormulaId);
        const json = await response.json() as DeleteDASAllocationFormulaResponse;
        return { allocationFormula: json?.allocationFormula ?? null };
    }

    /**
     * Triggers a full end-to-end materialisation of the sheet at the given definition version.
     * `-1` runs against the draft, `0` against the live version. Returns an execution id the
     * caller can poll via `getDerivedAllocationSheetExecutionStatus`.
     */
    async executeDerivedAllocationSheet(request: ExecuteDerivedAllocationSheetRequest): Promise<ExecuteDerivedAllocationSheetResponse> {
        const response = await apiService.executeDerivedAllocationSheet(request);
        const json = await response.json() as ExecuteDerivedAllocationSheetResponse;
        return { executionId: json?.executionId ?? null };
    }

    /**
     * Reads the current execution status for a sheet. Pass `executionId` to pin a specific run;
     * omit to get the latest execution on the sheet. Used by Execute-button polling and by cold
     * page loads to resync after a WebSocket drop.
     */
    async getDerivedAllocationSheetExecutionStatus(request: GetDerivedAllocationSheetExecutionStatusRequest): Promise<GetDerivedAllocationSheetExecutionStatusResponse> {
        const response = await apiService.getDerivedAllocationSheetExecutionStatus(request);
        const json = await response.json() as GetDerivedAllocationSheetExecutionStatusResponse;
        return json ?? {};
    }

    /**
     * Returns a presigned S3 URL to the materialised output workbook. `version = -1` for the
     * draft preview, `0` for live, omit for whichever lane the working definition points at.
     */
    async downloadDerivedAllocationSheet(request: DownloadDerivedAllocationSheetRequest): Promise<DownloadDerivedAllocationSheetResponse> {
        const response = await apiService.downloadDerivedAllocationSheet(request);
        const json = await response.json() as DownloadDerivedAllocationSheetResponse;
        return { downloadUrl: json?.downloadUrl ?? null };
    }

    /**
     * Sets or replaces the Step-4 mapping on a sheet. Backend derives `segmentType` from the
     * referenced Segment CDT and returns the updated `sheetData`.
     */
    async setDerivedAllocationSheetMapping(request: SetDASMappingRequest): Promise<SetDASMappingResponse> {
        const response = await apiService.setDerivedAllocationSheetMapping(request);
        const json = await response.json() as SetDASMappingResponse;
        return { sheetData: json?.sheetData ? this.transformSingleSheet(json.sheetData) : null };
    }

    private formatDate(timestamp: number | string): string {
        if (!timestamp) return '-';
        const date = typeof timestamp === 'number'
            ? new Date(timestamp)
            : new Date(Number.isFinite(Number(timestamp)) ? Number(timestamp) : timestamp);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    }

    private transformSingleSheet(sheet: any): DerivedAllocationSheetData {
        return {
            ...sheet,
            createdAt: this.formatDate(sheet.createdAt),
            lastModifiedDate: this.formatDate(sheet.lastModifiedDate),
            submitDate: this.formatDate(sheet.submitDate),
            reviewDate: this.formatDate(sheet.reviewDate),
        };
    }

    private transformSheetList(sheets: any[]): DerivedAllocationSheetData[] {
        return sheets.map(s => this.transformSingleSheet(s));
    }
}
